import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useSelector } from "react-redux";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import ConfirmDialog from "../../../Molecules/Common/ProfessionalContacts/ConfirmDialog";
import IconAction from "./IconAction";
import FirmDocumentsTable from "./FirmDocumentsTable";
import FirmDocumentsAllDialog from "./FirmDocumentsAllDialog";
import FirmDocumentUploadDialog from "./FirmDocumentUploadDialog";
import { getFormattedDate } from "../../../../../Utils/Helpers/dateUtils";
import { getFileBadgeClass } from "../DocumentsDashboard/DocumentsDashboard.helper";
import "../DocumentsDashboard/DocumentsDashboard.css";
import { api, buildFirmDocumentsQueryParams } from "./ProfessionalContacts.helper";
import useStyles from "./ProfessionalContacts.styles";

// FIRM-DOCUMENTS-020 (LLD §0.4): the inline table shows at most this many rows; beyond it a
// "View all {count} documents" link opens FirmDocumentsAllDialog (FIRM-DOCUMENTS-033: its own
// server-side page, independent of this preview). FIRM-DOCUMENTS-032 (v1.12.0): this is now also
// the exact pageSize the section's own fetch requests — `documents` state holds the preview rows
// only, never the full list.
const DOCUMENTS_PREVIEW_LIMIT = 5;

const extensionOf = (document) => {
  const raw =
    document.fileExtension ||
    (document.fileName || "").slice((document.fileName || "").lastIndexOf("."));
  return (raw || "").replace(".", "").toLowerCase();
};

/**
 * The firm record's Documents section (LLD §3.2): owns its own fetching, the 0/1/2+ rendering
 * split, every list-load state from HLD §4a.3, and the download/delete flows — including the
 * API-first, blob-second delete ordering (HLD §5 F4) that never leaves a dangling row visible.
 */
const FirmDocumentsSection = ({ firmId, documentCount }) => {
  const classes = useStyles();
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);

  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [allDialogOpen, setAllDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, document: null });
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    // Known-empty (the firm aggregate's 9th result set already said so): render the empty state,
    // make no call at all (R11). `undefined` (an API without that result set yet) falls through.
    if (documentCount === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    api
      .listFirmDocuments(
        firmId,
        buildFirmDocumentsQueryParams({
          pageSize: DOCUMENTS_PREVIEW_LIMIT,
          sort: "uploadedUtc",
          sortDir: "desc",
        })
      )
      .then((page) => {
        if (cancelled) return;
        setDocuments((page && page.items) || []);
        setTotal(page && page.total);
      })
      .catch((err) => {
        if (cancelled) return;
        // A 404 here means the feature isn't deployed yet — the UI ships ahead of the API by
        // rendering nothing rather than an error (R10). Any other error is shown inline below.
        if (err && err.status === 404) setNotFound(true);
        else setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmId, documentCount, refreshToken]);

  if (notFound) return null;

  // FIRM-DOCUMENTS-032 (v1.12.0): `total` comes from the same paginated fetch that populates
  // `documents`, so once a fetch has actually completed it is always at least as current as the
  // `documentCount` prop (which is only as fresh as the firm aggregate's last load) — prefer it.
  // `documentCount` is the fallback for the gap before/during the first fetch (or when the route
  // 404s before this component even tries), the same role it always played.
  const count = total !== undefined ? total : documentCount;

  const handleRetry = () => setRefreshToken((token) => token + 1);

  // FIRM-DOCUMENTS-032 (v1.12.0): a deliberate departure from this feature's "mutate local state,
  // never refetch" convention (HLD §4a.4/§4a.5) — now that `documents` is only a 5-row preview
  // (not the full list), a local prepend/filter can no longer correctly decide which row the
  // mutation should push in or out of that preview; only the server can, so re-request the preview.
  const refreshPreview = () => setRefreshToken((token) => token + 1);

  // FIRM-DOCUMENTS-027: the dialog uploads a batch and reports every document that succeeded in
  // one call, not one document at a time. FIRM-DOCUMENTS-032: refetches the preview instead of
  // prepending locally — see refreshPreview.
  const handleUploaded = () => {
    refreshPreview();
  };

  const handleDownload = (document) => {
    setBusyId(document.documentId);
    setActionError(null);
    api
      .downloadFirmDocument(document, oidcUser)
      .catch(() => {
        // downloadFirmDocument's underlying Document Helper call discards the original error
        // (see DocumentsDashboard.helper.js's downloadDocument), so a missing blob and any other
        // download failure are indistinguishable here — both get the same generic message.
        setActionError("Download failed. Please try again.");
      })
      .finally(() => setBusyId(null));
  };

  const handleConfirmDelete = async () => {
    const document = confirm.document;
    if (!document) return;
    setBusyId(document.documentId);
    setActionError(null);
    try {
      const deleted = await api.deleteFirmDocument(firmId, document.documentId);
      // Best-effort: a storage failure must never resurrect a row the API has already deleted
      // (HLD §5 F4) — API first, blob second, always in that order.
      api.deleteFirmDocumentBlob(deleted.filePath, oidcUser).catch(() => {});
      refreshPreview();
    } catch (err) {
      setActionError(err.message || "Delete failed. Please try again.");
    } finally {
      setBusyId(null);
      setConfirm({ open: false, document: null });
    }
  };

  const singleDocumentRow = () => {
    const document = documents[0];
    const busy = busyId === document.documentId;
    return (
      <div className={classes.rowItem}>
        <div className={classes.rowGrow}>
          <span className={classes.documentNameCell} title={document.fileName}>
            <span
              className={clsx(
                "fileTypeBadge",
                classes.documentIcon,
                getFileBadgeClass(extensionOf(document))
              )}
            >
              {extensionOf(document) || "file"}
            </span>
            <span className={classes.cellStrong}>{document.fileName}</span>
          </span>
          <div className={classes.subtle}>
            {document.uploadedBy} ·{" "}
            {getFormattedDate(document.uploadedUtc, "DD/MM/YYYY, HH:mm")}
          </div>
        </div>
        <IconAction
          title={`Download ${document.fileName}`}
          icon={<GetAppIcon fontSize="small" />}
          disabled={busy}
          onClick={() => handleDownload(document)}
        />
        <IconAction
          danger
          title={`Delete ${document.fileName}`}
          icon={<DeleteIcon fontSize="small" />}
          disabled={busy}
          onClick={() => setConfirm({ open: true, document })}
        />
      </div>
    );
  };

  return (
    <div>
      <div className={classes.sectionHeading}>
        Documents
        {Boolean(count) && <span className={classes.countChip}>{count}</span>}
        <button
          type="button"
          className={clsx(classes.addRowButton, classes.headingAction)}
          onClick={() => setUploadOpen(true)}
        >
          <AddIcon fontSize="inherit" /> Add documents
        </button>
      </div>

      {loading ? (
        <div className={classes.subtle}>Loading…</div>
      ) : error ? (
        <div className={classes.subtle}>
          Couldn&rsquo;t load documents.{" "}
          <button
            type="button"
            className={classes.linkAction}
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      ) : documents.length === 0 ? (
        <M_DataStateRenderer isEmpty emptyMessage="No documents yet." />
      ) : documents.length === 1 ? (
        singleDocumentRow()
      ) : (
        <>
          <FirmDocumentsTable
            documents={documents}
            onDownload={handleDownload}
            onDelete={(document) => setConfirm({ open: true, document })}
            busyId={busyId}
          />
          <button
            type="button"
            className={classes.linkAction}
            onClick={() => setAllDialogOpen(true)}
          >
            View all {count} documents
          </button>
        </>
      )}

      {actionError && (
        <div
          className={classes.subtle}
          style={{ color: "var(--status-error-color)", marginTop: 4 }}
        >
          {actionError}
        </div>
      )}

      <FirmDocumentUploadDialog
        firmId={firmId}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={handleUploaded}
      />

      {allDialogOpen && (
        <FirmDocumentsAllDialog
          open={allDialogOpen}
          onClose={() => setAllDialogOpen(false)}
          firmId={firmId}
          refreshToken={refreshToken}
          onDownload={handleDownload}
          onDelete={(document) => setConfirm({ open: true, document })}
          busyId={busyId}
        />
      )}

      <ConfirmDialog
        open={confirm.open}
        mode="confirm"
        title="Delete document"
        message={
          confirm.document && (
            <>
              Delete &lsquo;<b>{confirm.document.fileName}</b>&rsquo;? This
              removes the file from storage. A record of the deletion is
              retained for audit.
            </>
          )
        }
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirm({ open: false, document: null })}
      />
    </div>
  );
};

FirmDocumentsSection.propTypes = {
  firmId: PropTypes.string.isRequired,
  documentCount: PropTypes.number,
};

export default FirmDocumentsSection;
